<template>
  <div class="container">
    <div class="top" :style="{ backgroundImage: 'url(' + backgroundUrl + ')' }">
      <icon-cross
        @click.native="cancelAction"
        class="top-exit"
        crossColor="#fff"
        tabindex="0"
        @keyup.native.enter="cancelAction"
      />
      <div v-if="userInManualSale" class="top-congrat-manual-container" tabindex="-1">
        <p class="top-congrat-text" aria-hidden="true">{{ congratulationText }}</p>
      </div>
      <div class="top-congrat-container" ref="congrat-container" tabindex="-1" :aria-label="userInManualSale ? fullScreenDescription : parseDomToText($t(`app.sales.autoAndManual.title${textAddition}`))">
        <p v-if="userInManualSale" class="top-congrat-text manual-text" aria-hidden="true">
          {{ fullScreenDescription }}
        </p>
        <p
          ref="congrat-text"
          v-else
          class="top-congrat-text text-with-image"
          v-html="$t(`app.sales.autoAndManual.title${textAddition}`)"
        ></p>
      </div>
    </div>
    <div class="bottom">
      <div class="bottom-list">
        <div
          class="bottom-list-container"
          :class="{ 'is-timer': isTimerAvailable }"
        >
          <template v-if="isTimerAvailable">
            <div class="bottom-list-element element-with-margin">
              <IconSalesTimer />
              <span tabindex="-1" :aria-label="$t('app.sales.autoAndManual.timer')">{{ $t("app.sales.autoAndManual.timer") }}</span>
            </div>
            <div class="bottom-list-element element-with-margin">
              <IconSalesNoAds />
              <span tabindex="-1" :aria-label="$t('app.sales.autoAndManual.noADS')">{{ $t("app.sales.autoAndManual.noADS") }}</span>
            </div>
            <div class="bottom-list-element element-with-margin">
              <IconSalesLanguages />
              <span tabindex="-1" :aria-label="$t('app.sales.autoAndManual.allLanguages')">{{ $t("app.sales.autoAndManual.allLanguages") }}</span>
            </div>
            <div class="language-list" tabindex="-1" :aria-label="$t('app.sales.autoAndManual.languagesList')">
              {{ $t("app.sales.autoAndManual.languagesList") }}
            </div>
          </template>
          <template v-else>
            <div class="bottom-list-element element-with-margin">
              <IconSalesSpeakBot />
              <span tabindex="-1" :aria-label="$t('app.sales.autoAndManual.speakBot')">{{
                $t("app.sales.autoAndManual.speakBot", { count: 150 })
              }}</span>
            </div>
            <div class="bottom-list-element element-with-margin">
              <IconSalesGrammar />
              <span tabindex="-1" :aria-label="$t('app.sales.autoAndManual.grammar')">{{
                $t("app.sales.autoAndManual.grammar", { count: 300 })
              }}</span>
            </div>
            <div class="bottom-list-element element-with-margin">
              <IconSalesWordCategory />
              <span tabindex="-1" :aria-label="$t('app.sales.autoAndManual.wordCategory')">{{
                $t("app.sales.autoAndManual.wordCategory", { count: 24 })
              }}</span>
            </div>
            <div class="bottom-list-element">
              <IconSalesWordExpression />
              <span tabindex="-1" :aria-label="$t('app.sales.autoAndManual.expressions')">{{
                $t("app.sales.autoAndManual.expressions", { count: 9000 })
              }}</span>
            </div>
          </template>
        </div>
      </div>
      <div class="bottom-offer">
        <rounded-button
          @roundedButtonClicked="okButtonClicked"
          :text="$t(getAutoManualSaleButtonText)"
          :isTextVhtml="isOkButtonTextHtml"
          class="button"
          :fontSize="'10px'"
          :fontWeight="'bold'"
          :state="'enabled'"
          :color="'#fab62f'"
          :borderRadius="'26px'"
          :customStyle="{ height: '32px' }"
        />
        <p v-if="product" class="bottom-offer-description" tabindex="-1" :aria-label="$t(getAutoManualSaleButtonLabel)">
          {{ $t(getAutoManualSaleButtonLabel) }}
          <inline-link v-if="hasDiscount" class="more-about-price" :text="$t('app.priceCommunication.link.text')" @inlineLinkClicked="isLoading ? '' : openPriceCommunication()"/>
        </p>
        <discount-label
          v-if="hasDiscount"
          :text="getDiscountPercentageText"
          :rotateValue="5"
          :textColor="'#fff'"
          :padding="'0 0 0 10px'"
          :labelHeight="31"
          :labelWidth="75"
          :rightSideBackgroundColor="'blue'"
          :isLeftSide="false"
          class="discount-label"
        />
      </div>
      <p @click="cancelAction" tabindex="0" @keyup.enter="cancelAction" class="bottom-no-thanks" :aria-label="$t('app.shared.noThanks')">
        {{ $t("app.shared.noThanks") }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PriceCommunicationModal from '@/components/shared/elements/modal/PriceCommunicationModal';
import RoundedButton from '@/components/shared/elements/buttons/RoundedButton';
import DiscountLabel from '@/components/shared/elements/modal/DiscountLabel';
import ProductsContentMixin from '@/components/shared/elements/mixins/ProductsContentMixin';
import IconCross from '../icons/IconCross';
import IconSalesTimer from '../icons/IconSalesTimer';
import IconSalesNoAds from '../icons/IconSalesNoAds';
import IconSalesLanguages from '../icons/IconSalesLanguages';
import IconSalesGrammar from '../icons/IconSalesGrammar';
import IconSalesSpeakBot from '../icons/IconSalesSpeakBot';
import IconSalesWordCategory from '../icons/IconSalesWordCategory';
import IconSalesWordExpression from '../icons/IconSalesWordExpression';

export default {
  name: 'SalesLargeModal',
  mixins: [ProductsContentMixin],
  components: {
    IconCross,
    RoundedButton,
    DiscountLabel,
    IconSalesGrammar,
    IconSalesSpeakBot,
    IconSalesWordCategory,
    IconSalesWordExpression,
    IconSalesTimer,
    IconSalesNoAds,
    IconSalesLanguages,
    PriceCommunicationModal,
  },
  props: {
    okAction: {
      required: true,
      type: Function,
      default: () => {
      },
    },
    cancelAction: {
      required: true,
      type: Function,
      default: () => {
      },
    },
    backgroundUrl: {
      required: true,
      type: String,
      default:
        'https://s3.eu-central-1.amazonaws.com/staging-xeropan-assets/static_files/sale_campaign/streak/mobil/test/a.png',
    },
    congratulationText: {
      required: true,
      type: String,
      default: 'Congratulations, Attila AlGharawi!',
    },
    okText: {
      required: true,
      type: String,
      default: 'Get 1 year PRO only 990 Ft / month',
    },
    fullScreenDescription: {
      required: true,
      type: String,
      default:
        "You're on a 3-day learning streak🔥 </br> Here's your special offer!",
    },
    renewHint: {
      required: true,
      type: String,
      default: 'Annual subscription',
    },
    percentageOff: {
      required: true,
      type: Number,
      default: 50,
    },
    type: {
      type: String,
      required: true,
    },
    userInManualSale: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      analyticsActionName: '',
    };
  },
  mounted() {
    setTimeout(() => {
      this.titleAriaLabel();
    }, 500);
  },
  methods: {
    ...mapActions({
      getProduct: 'subscriptions/getProduct',
      getExchangeRate: 'subscriptions/getExchangeRate',
    }),
    openPriceCommunication() {
      this.$eventBus.showModal({
        bind: {
          is: PriceCommunicationModal,
          product: this.product,
          monthlyProduct: this.currentProducts[0],
          currency: this.currency,
          discountText: `${this.product.discountPercentage}%`,
        },
      });
    },
    titleAriaLabel() {
      const title = this.$refs['congrat-text']?.innerHTML;
      const reg = /<img\s[^>]*?src\s*=\s*['\"]([^'\"]*?)['\"][^>]*?>/;
      const forAriaLabel = title.replace(reg, this.$t('app.general.texts.general.pro'));
      this.$refs['congrat-container'].setAttribute('aria-label', forAriaLabel);
    },
    parseDomToText(text) {
      const parser = new DOMParser();
      const parsedText = parser.parseFromString(text, 'text/html');
      return parsedText.documentElement.textContent;
    },
    okButtonClicked() {
      this.okAction(this.product);
    },
    setAnalitycs(value) {
      if (value > 5) {
        if (!this.product.hasTrialPeriod) {
          this.analyticsActionName = 'Manual Sale';
          this.sendAnalytics(false);
        } else {
          this.analyticsActionName = 'Manual Sale';
          this.sendAnalytics(true);
        }
      } else {
        if (value == 1) {
          this.analyticsActionName = 'Sale Popup';
          this.sendAnalytics(false);

          // facebook pixel measurement point
          if (typeof fbq !== 'undefined') {
            fbq('trackCustom', 'Sale Popup', {
              eventCategory: 'PRO offer',
              eventLabel: this.product.analyticKey,
            });
          }
        }
        if (value == 2) {
          this.analyticsActionName = 'Milestone Rank Autosale';
          this.sendAnalytics(false);
        }
      }
    },
    sendAnalytics(isManualWithTrial) {
      if (!isManualWithTrial) {
        // gtag measurement point
        if (this.$gtag) {
          this.$gtag.event(this.analyticsActionName, {
            event_category: 'PRO offer',
            event_label: this.product.analyticKey,
          });
        }
      } else if (this.$gtag) {
        // gtag measurement point
        this.$gtag.event(this.analyticsActionName, {
          event_category: 'PRO offer',
          event_label: [this.product.analyticKey, this.product.hasTrialPeriod],
        });
      }
    },
  },
  computed: {
    ...mapGetters({
      isTimerAvailable: 'users/isTimerAvailable',
      getSelectedLearnedLanguage: 'settings/getSelectedLearnedLanguage',
    }),
    textAddition() {
      if (!this.getSelectedLearnedLanguage) {
        return '';
      }
      if (this.getSelectedLearnedLanguage === 'en' || this.getSelectedLearnedLanguage.startsWith('esp')) {
        return '';
      }
      return `.${this.getSelectedLearnedLanguage}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  padding: 0;
  position: relative;
  width: 283.6px;

  .top {
    padding: 0;
    margin: 0;
    min-height: 201px;
    position: relative;
    background-clip: border-box;
    background-size: cover;
    background-repeat: no-repeat;
    border-radius: 4px 4px 0 0;

    .top-exit {
      margin: 0;
      position: absolute;
      right: 16px;
      top: 15px;
      display: inline-block;
      cursor: pointer;
      width: 18px;
      height: 18px;
    }

    .text-with-image {
      /deep/ img {
        border-radius: 2px;
      }
    }

    .top-congrat-container {
      position: absolute;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1;
      border-radius: 8px;
      margin: auto;
      bottom: 17px;
      // top: 135px;
    }
    .top-congrat-manual-container {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1;
      border-radius: 8px;
      margin: auto;
      padding-top: 40px;
      .top-congrat-text {
        font-weight: bold !important;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 15px !important;
        background-color: rgba(0, 0, 0, 0.41);
        width: 207px;
        height: 77px;
      }
    }

    .top-congrat-text {
      width: 210px;
      margin: 0 auto;
      padding: 0;
      display: inline-block;
      font-family: Roboto;
      font-size: 17px;
      font-weight: 500 !important;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.33;
      letter-spacing: normal;
      text-align: center;
      color: var(--white);
      border-radius: 8px;
    }

    .manual-text {
      font-weight: 400 !important;
      width: 246px;
    }
  }

  .bottom {
    padding-top: 16px;
    padding-left: 18px;
    padding-right: 18px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    min-height: 240px;
    background-color: white;
    border-radius: 0 0 4px 4px;
    p {
      word-wrap: break-word;
    }

    .bottom-list {
      display: flex;
      flex-direction: column;
      align-items: center;
      font-size: 12px;
      color: var(--terraGrey);
      span {
        margin-left: 9px;
      }

      .is-timer {
        .bottom-list-element {
          justify-content: center;
          text-align: center;
        }
        .language-list {
          font-size: 12px;
          text-align: center;
        }
      }

      .bottom-list-element {
        align-items: center;
        display: flex;
      }

      .element-with-margin {
        margin-bottom: 6px;
      }
    }
    .bottom-streak {
      p {
        font-family: Roboto;
        font-size: 14px;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.36;
        letter-spacing: normal;
        text-align: center;
        color: var(--terraGrey);
        margin: 0;
        padding: 15px 14px 0 12px;
      }
    }

    .bottom-offer {
      position: relative;
      line-height: 10px;
      padding-top: 16px;
      /deep/ .button {
        padding: 0 !important;
      }
      .bottom-offer-description {
        font-size: 10px;
        width: 230px;
        text-align: center;
        font-family: Roboto;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.25;
        letter-spacing: normal;
        color: var(--terraGrey);
        padding-top: 8px;
        margin: 0 auto;
      }

      .more-about-price {
        height: 16px !important;
        margin: 0px auto 8px auto;
        text-align: center;
        /deep/ .link {
          font-size: 10px;
          line-height: 1.25;
          font-weight: normal;
          letter-spacing: normal;
          font-style: normal;
        }
        :hover {
          text-decoration: unset !important;
        }
      }

      .bottom-offer-percentage {
        position: absolute;
        top: 0px;
        padding: 6px 10px;
        right: 11px;
        font-family: Roboto;
        font-size: 12px;
        font-weight: bold;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.33;
        letter-spacing: normal;
        text-align: center;
        color: var(--white);
        background-color: rgba(0, 0, 0, 0.41);
        border-radius: 28px;
      }
    }

    .discount-label {
      position: absolute;
      right: -2px;
      top: 0px;
      transform: scale(0.8);
      @media (min-width: $medium-screen) {
        right: -20px;
      }
    }

    .bottom-no-thanks {
      margin-top: 28px;
      margin-bottom: 24px;
      font-family: Roboto;
      font-size: 10px;
      font-weight: 500;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.3;
      letter-spacing: normal;
      text-align: center;
      color: var(--terraGrey);
      text-decoration: none;
      &:hover {
        color: #343434;
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }
  .button {
    padding: 0px 18.8px !important;
  }
}
</style>
